(() => {
  if (customElements.get('pdp-pair-details')) return;
  class PdpPairDetails extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.setupPrefetch(); return; }
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.cache = new Map();
      this.pending = new Map();
      this.prefetchHandles = new Set();
      this.prefetchJobs = new Set();
      this.warmedImages = new Set();
      this.requestVersion = 0;
      this.querySelector('[data-pair-close]').addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-pair-retry]').addEventListener('click', () => this.load(this.state));
      this.querySelector('[data-pair-select]').addEventListener('click', () => {
        if (!this.state || this.state.disabled) return;
        if (!this.state.selected && !this.state.inCart) this.select(true);
        this.dialog.close();
      });
      this.querySelector('[data-pair-remove]').addEventListener('click', () => {
        if (!this.state || this.state.busy) return;
        this.select(false);
        this.dialog.close();
      });
      this.querySelector('[data-pair-link]').addEventListener('click', () => this.dispatchEvent(new CustomEvent('pair-details:full-page', {bubbles: true, detail: {handle: this.state.handle}})));
      let backdrop = false;
      this.dialog.addEventListener('pointerdown', event => {
        const r = this.dialog.getBoundingClientRect();
        backdrop = event.target === this.dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
      });
      this.dialog.addEventListener('click', event => {
        if (backdrop && event.target === this.dialog) this.dialog.close();
        backdrop = false;
      });
      this.dialog.addEventListener('close', () => {
        this.requestVersion++;
        this.controller?.abort();
        document.documentElement.style.overflow = this.previousOverflow || '';
        const root = this.closest('[data-journey-pdp]');
        const trigger = this.trigger?.isConnected ? this.trigger : [...root.querySelectorAll('[data-journey-details]')].find(button => button.dataset.handle === this.state.handle);
        if (trigger) { trigger.setAttribute('aria-expanded', 'false'); trigger.focus({preventScroll: true}); }
      });
      this.setupPrefetch();
    }
    disconnectedCallback() {
      this.prefetchController?.abort();
      this.prefetchObserver?.disconnect();
      this.prefetchMutationObserver?.disconnect();
    }
    contentURL(rawURL) {
      const url = new URL(rawURL, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return null;
      url.hash = '';
      if (window.Shopify?.theme?.id && window.Shopify.theme.role !== 'main') url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
      const qa = new URLSearchParams(location.search).get('hinok_qa');
      if (qa) url.searchParams.set('hinok_qa', qa);
      return url.href;
    }
    setupPrefetch() {
      if (this.prefetchController && !this.prefetchController.signal.aborted) return;
      const root = this.closest('[data-journey-pdp]');
      if (!root) return;
      this.prefetchController = new AbortController();
      const {signal} = this.prefetchController;
      const intent = event => {
        const trigger = event.target.closest?.('[data-journey-details]');
        if (!trigger || !root.contains(trigger) || trigger.contains(event.relatedTarget)) return;
        this.prefetch(trigger);
      };
      root.addEventListener('pointerover', intent, {signal});
      root.addEventListener('focusin', intent, {signal});
      if (!('IntersectionObserver' in window)) return;
      const region = root.querySelector('[data-journey-fieldset]');
      if (!region) return;
      // Wait until the main page has loaded; warm only recommendations near view.
      let nearView = false;
      let scheduled = false;
      const warmNearView = () => {
        if (!nearView || scheduled) return;
        scheduled = true;
        const warm = () => {
          scheduled = false;
          if (signal.aborted || !nearView) return;
          for (const trigger of [...region.querySelectorAll('[data-journey-details]')].slice(0, 2)) this.prefetch(trigger);
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(warm, {timeout: 400});
        else setTimeout(warm, 0);
      };
      this.prefetchObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          nearView = entry.isIntersecting;
          warmNearView();
        }
      }, {rootMargin: '200px 0px'});
      // Recommendation rows can be replaced when a selection changes.
      this.prefetchMutationObserver = new MutationObserver(records => {
        if (records.some(record => record.addedNodes.length)) warmNearView();
      });
      this.prefetchMutationObserver.observe(region, {childList: true, subtree: true});
      const observe = () => { if (!signal.aborted) this.prefetchObserver.observe(region); };
      if (document.readyState === 'complete') observe();
      else window.addEventListener('load', observe, {once: true, signal});
    }
    prefetch(trigger) {
      const connection = navigator.connection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '') || document.visibilityState === 'hidden') return;
      const handle = trigger.dataset.handle;
      if (!handle || !trigger.isConnected) return;
      if (!this.prefetchHandles.has(handle) && this.prefetchHandles.size >= 2) return;
      const route = window.Shopify?.routes?.root || '/';
      const url = this.contentURL(`${route.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`);
      if (!url || this.prefetchJobs.has(url) || (this.pending.size >= 2 && !this.pending.has(url))) return;
      this.prefetchHandles.add(handle);
      this.prefetchJobs.add(url);
      this.fetchContent(url, handle)
        .then(content => { if (this.isConnected) this.warmImage(content, url); })
        .catch(() => {}) // Speculative failures must not surface in the dialog.
        .finally(() => this.prefetchJobs.delete(url));
    }
    warmImage(content, url) {
      const image = content.querySelector('[data-pair-product-media]')?.querySelector('img');
      if (!image) return;
      const source = [...(image.closest('picture')?.querySelectorAll('source') || [])].find(item => !item.media || window.matchMedia(item.media).matches);
      const srcset = source?.getAttribute('srcset') || image.getAttribute('srcset');
      const sizes = source?.getAttribute('sizes') || image.getAttribute('sizes');
      const key = `${url}|${srcset || image.getAttribute('src')}`;
      if (this.warmedImages.has(key)) return;
      this.warmedImages.add(key);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.setAttribute('fetchpriority', 'low');
      if (srcset) link.setAttribute('imagesrcset', srcset);
      if (sizes) link.setAttribute('imagesizes', sizes);
      link.href = source ? srcset.split(',')[0].trim().split(/\s+/)[0] : image.getAttribute('src');
      const cleanup = () => link.remove();
      link.addEventListener('load', cleanup, {once: true});
      link.addEventListener('error', cleanup, {once: true});
      document.head.append(link);
    }
    fetchContent(url, handle) {
      if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));
      if (this.pending.has(url)) return this.pending.get(url);
      // This request can serve hover, click and reopen. Closing only cancels UI work.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      const request = (async () => {
        try {
          const response = await fetch(url, {headers: {Accept: 'text/html'}, credentials: 'same-origin', signal: controller.signal});
          if (!response.ok) throw new Error('Product details unavailable');
          const container = document.createElement('template');
          container.innerHTML = await response.text();
          const template = container.content.querySelector('[data-pair-product-content]');
          if (!template || template.dataset.productHandle !== handle) throw new Error('Missing product details');
          const content = template.content;
          if (!content.querySelector('[data-pair-product-media]') || !content.querySelector('[data-pair-product-copy]')) throw new Error('Missing product content');
          this.cache.set(url, content);
          return content;
        } finally { clearTimeout(timeout); }
      })().finally(() => { if (this.pending.get(url) === request) this.pending.delete(url); });
      this.pending.set(url, request);
      return request;
    }
    select(selected) {
      this.dispatchEvent(new CustomEvent('pair-details:select', {bubbles: true, detail: {handle: this.state.handle, selected}}));
    }
    open(trigger, state) {
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      this.update(state);
      this.querySelector('[data-pair-title]').textContent = state.title;
      this.querySelector('[data-pair-volume]').textContent = state.volume;
      const url = this.contentURL(state.url);
      if (!url) return;
      this.querySelector('[data-pair-link]').href = url;
      if (!this.dialog.open) {
        this.previousOverflow = document.documentElement.style.overflow;
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }
      this.querySelector('[data-pair-scroll]').scrollTop = 0;
      this.load(state);
    }
    update(state) {
      if (!state) { this.dialog.close(); return; }
      this.state = state;
      const select = this.querySelector('[data-pair-select]');
      const selected = state.selected || state.inCart;
      this.dialog.dataset.selected = String(selected);
      this.dialog.dataset.soldOut = String(state.soldOut);
      select.disabled = state.disabled;
      select.hidden = state.soldOut;
      this.querySelector('[data-pair-select-label]').textContent = state.inCart ? select.dataset.cartLabel : state.selected ? select.dataset.keepLabel : select.dataset.addLabel;
      this.querySelector('[data-pair-add-price]').textContent = selected || state.soldOut ? '' : '+' + state.price;
      this.querySelector('[data-pair-current]').textContent = state.currentTotal;
      this.querySelector('[data-pair-total]').textContent = state.total;
      this.querySelector('[data-pair-summary]').hidden = state.soldOut;
      this.querySelector('[data-pair-prospective]').hidden = selected;
      this.querySelector('[data-pair-selected]').hidden = !selected || state.soldOut;
      this.querySelector('[data-pair-remove]').hidden = !state.selected;
      this.querySelector('[data-pair-remove]').disabled = state.busy;
      this.querySelector('[data-pair-remove-price]').textContent = '−' + state.price;
      this.querySelector('[data-pair-stock]').hidden = !state.soldOut;
      const price = this.querySelector('[data-pair-price]');
      price.textContent = state.soldOut ? price.dataset.soldOut : state.price;
      const notify = this.querySelector('[data-pair-notify]');
      notify.hidden = !state.soldOut;
      notify.dataset.variantId = state.variantId;
      notify.dataset.productId = state.productId || '';
      notify.dataset.productTitle = state.title;
      notify.dataset.productVolume = state.volume;
      notify.dataset.productHandle = state.handle;
    }
    setLoadState(value) {
      this.dialog.dataset.state = value;
      this.querySelector('[data-pair-loading]').hidden = value !== 'loading';
      this.querySelector('[data-pair-error]').hidden = value !== 'error';
      this.querySelector('[data-pair-loaded]').hidden = value !== 'ready';
      this.querySelector('[data-pair-actions]').hidden = value !== 'ready';
    }
    initImage(signal) {
      const media = this.querySelector('[data-pair-media]');
      const image = media.querySelector('img');
      if (!image) return;
      const unavailable = () => {
        image.hidden = true;
        media.querySelector('[data-pair-image-fallback]').hidden = false;
      };
      image.addEventListener('error', unavailable, {once: true, signal});
      if (image.complete && !image.naturalWidth) unavailable();
    }
    async load(state) {
      const version = ++this.requestVersion;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const url = this.querySelector('[data-pair-link]').href;
      if (!this.cache.has(url)) this.setLoadState('loading');
      try {
        const content = this.cache.get(url) || await this.fetchContent(url, state.handle);
        if (version !== this.requestVersion || !this.dialog.open) return;
        const media = content.querySelector('[data-pair-product-media]');
        const copy = content.querySelector('[data-pair-product-copy]');
        if (!media || !copy) throw new Error('Missing product content');
        this.querySelector('[data-pair-media]').replaceChildren(...[...media.childNodes].map(node => node.cloneNode(true)));
        this.querySelector('[data-pair-content]').replaceChildren(...[...copy.childNodes].map(node => node.cloneNode(true)));
        this.setLoadState('ready');
        this.initImage(controller.signal);
      } catch (error) {
        if (version === this.requestVersion && this.dialog.open) this.setLoadState('error');
      }
    }
  }
  customElements.define('pdp-pair-details', PdpPairDetails);
})();
